import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import {
  useCallback
} from 'react';

import type {
  CreateArticleModel,
  CreatePrefabModel,
  GetAllArticlesParams,
  GetAllArticlesResponse,
  GetArticleByIdResponse,
  GetArticleByNumberResponse,
  GetArticleMetricsParams,
  GetArticleMetricsResponse,
  GetArticlesByElementTypeResponse,
  GetArticlesBySettingTypeParams,
  GetArticlesBySettingTypeResponse,
  GetArticlesBySystemIdResponse,
  GetArticlesBySystemParams,
  GetMainRepresentationByArticleIdParams,
  GetMainRepresentationByArticleIdResponse,
  GetPrefabSummariesByArticleIdResponse,
  ImportPrefabByArticleIdParams,
  ProblemDetails,
  SavePrefabTransformationForCadModel,
  SavePrefabTransformationForCadResponse,
  UpdatePrefabModel
} from '../../models';

import { useCustomAppClient } from '../../custom-client.tsx';
import type { ErrorType } from '../../custom-client.tsx';




const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * Get article by id
 */
export const useGetArticleByIdHook = () => {
        const getArticleById = useCustomAppClient<GetArticleByIdResponse>();

        return useCallback((
    id: string,
 signal?: AbortSignal
) => {


        return getArticleById(
          {url: `/articles/${id}`, method: 'GET', signal
    },
          );
        }, [getArticleById])
      }




export const getGetArticleByIdQueryKey = (id: string,) => {
    return [
    `/articles/${id}`
    ] as const;
    }


export const useGetArticleByIdQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>, TError = ErrorType<ProblemDetails>>(id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetArticleByIdQueryKey(id);

  const getArticleById =  useGetArticleByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>> = ({ signal }) => getArticleById(id, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetArticleByIdQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>>
export type GetArticleByIdQueryError = ErrorType<ProblemDetails>


export function useGetArticleById<TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetArticleById<TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetArticleById<TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetArticleById<TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByIdHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetArticleByIdQueryOptions(id,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Delete article by id
 */
export const useDeleteArticleByIdHook = () => {
        const deleteArticleById = useCustomAppClient<void>();

        return useCallback((
    id: string,
 signal?: AbortSignal
) => {


        return deleteArticleById(
          {url: `/articles/${id}`, method: 'DELETE', signal
    },
          );
        }, [deleteArticleById])
      }




export const getDeleteArticleByIdMutationKey = () => ['deleteArticleById'] as const;

export const useDeleteArticleByIdMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeleteArticleByIdHook>>>, TError,DeleteArticleByIdMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeleteArticleByIdHook>>>, TError,DeleteArticleByIdMutationVariables, TContext> => {

const mutationKey = getDeleteArticleByIdMutationKey();
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const deleteArticleById =  useDeleteArticleByIdHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useDeleteArticleByIdHook>>>, DeleteArticleByIdMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deleteArticleById(id,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteArticleByIdMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeleteArticleByIdHook>>>>

    export type DeleteArticleByIdMutationError = ErrorType<ProblemDetails>
    export type DeleteArticleByIdMutationVariables = {id: string}

    export const useDeleteArticleById = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeleteArticleByIdHook>>>, TError,DeleteArticleByIdMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useDeleteArticleByIdHook>>>,
        TError,
        DeleteArticleByIdMutationVariables,
        TContext
      > => {
      return useMutation(useDeleteArticleByIdMutationOptions(options), queryClient);
    }
    /**
 * Get article by articleNumber
 */
export const useGetArticleByNumberHook = () => {
        const getArticleByNumber = useCustomAppClient<GetArticleByNumberResponse>();

        return useCallback((
    articleNumber: string,
 signal?: AbortSignal
) => {


        return getArticleByNumber(
          {url: `/articles/${articleNumber}`, method: 'GET', signal
    },
          );
        }, [getArticleByNumber])
      }




export const getGetArticleByNumberQueryKey = (articleNumber: string,) => {
    return [
    `/articles/${articleNumber}`
    ] as const;
    }


export const useGetArticleByNumberQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>, TError = ErrorType<ProblemDetails>>(articleNumber: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetArticleByNumberQueryKey(articleNumber);

  const getArticleByNumber =  useGetArticleByNumberHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>> = ({ signal }) => getArticleByNumber(articleNumber, signal);





   return  { queryKey, queryFn, enabled: articleNumber !== null && articleNumber !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetArticleByNumberQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>>
export type GetArticleByNumberQueryError = ErrorType<ProblemDetails>


export function useGetArticleByNumber<TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>, TError = ErrorType<ProblemDetails>>(
 articleNumber: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetArticleByNumber<TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>, TError = ErrorType<ProblemDetails>>(
 articleNumber: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetArticleByNumber<TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>, TError = ErrorType<ProblemDetails>>(
 articleNumber: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetArticleByNumber<TData = Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>, TError = ErrorType<ProblemDetails>>(
 articleNumber: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleByNumberHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetArticleByNumberQueryOptions(articleNumber,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Get article metrics by article numbers
 */
export const useGetArticleMetricsHook = () => {
        const getArticleMetrics = useCustomAppClient<GetArticleMetricsResponse>();

        return useCallback((
    getArticleMetricsBody: string[],
    params: GetArticleMetricsParams,
 signal?: AbortSignal
) => {


        return getArticleMetrics(
          {url: `/articles/metrics`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: getArticleMetricsBody,
        params, signal
    },
          );
        }, [getArticleMetrics])
      }




export const getGetArticleMetricsMutationKey = () => ['getArticleMetrics'] as const;

export const useGetArticleMetricsMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleMetricsHook>>>, TError,GetArticleMetricsMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleMetricsHook>>>, TError,GetArticleMetricsMutationVariables, TContext> => {

const mutationKey = getGetArticleMetricsMutationKey();
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const getArticleMetrics =  useGetArticleMetricsHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useGetArticleMetricsHook>>>, GetArticleMetricsMutationVariables> = (props) => {
          const {data,params} = props ?? {};

          return  getArticleMetrics(data,params,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type GetArticleMetricsMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetArticleMetricsHook>>>>
    export type GetArticleMetricsMutationBody = string[]
    export type GetArticleMetricsMutationError = ErrorType<ProblemDetails>
    export type GetArticleMetricsMutationVariables = {data: string[];params: GetArticleMetricsParams}

    export const useGetArticleMetrics = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useGetArticleMetricsHook>>>, TError,GetArticleMetricsMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useGetArticleMetricsHook>>>,
        TError,
        GetArticleMetricsMutationVariables,
        TContext
      > => {
      return useMutation(useGetArticleMetricsMutationOptions(options), queryClient);
    }
    /**
 * Get articles by system id
 */
export const useGetArticlesBySystemHook = () => {
        const getArticlesBySystem = useCustomAppClient<GetArticlesBySystemIdResponse>();

        return useCallback((
    systemId: string,
    params?: GetArticlesBySystemParams,
 signal?: AbortSignal
) => {


        return getArticlesBySystem(
          {url: `/articles/system/${systemId}`, method: 'GET',
        params, signal
    },
          );
        }, [getArticlesBySystem])
      }




export const getGetArticlesBySystemQueryKey = (systemId: string,
    params?: GetArticlesBySystemParams,) => {
    return [
    `/articles/system/${systemId}`, ...(params ? [params] : [])
    ] as const;
    }


export const useGetArticlesBySystemQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>, TError = ErrorType<ProblemDetails>>(systemId: string,
    params?: GetArticlesBySystemParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetArticlesBySystemQueryKey(systemId,params);

  const getArticlesBySystem =  useGetArticlesBySystemHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>> = ({ signal }) => getArticlesBySystem(systemId,params, signal);





   return  { queryKey, queryFn, enabled: systemId !== null && systemId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetArticlesBySystemQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>>
export type GetArticlesBySystemQueryError = ErrorType<ProblemDetails>


export function useGetArticlesBySystem<TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>, TError = ErrorType<ProblemDetails>>(
 systemId: string,
    params: undefined |  GetArticlesBySystemParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetArticlesBySystem<TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>, TError = ErrorType<ProblemDetails>>(
 systemId: string,
    params?: GetArticlesBySystemParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetArticlesBySystem<TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>, TError = ErrorType<ProblemDetails>>(
 systemId: string,
    params?: GetArticlesBySystemParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetArticlesBySystem<TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>, TError = ErrorType<ProblemDetails>>(
 systemId: string,
    params?: GetArticlesBySystemParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySystemHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetArticlesBySystemQueryOptions(systemId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Get articles by setting type
 */
export const useGetArticlesBySettingTypeHook = () => {
        const getArticlesBySettingType = useCustomAppClient<GetArticlesBySettingTypeResponse>();

        return useCallback((
    params: GetArticlesBySettingTypeParams,
 signal?: AbortSignal
) => {


        return getArticlesBySettingType(
          {url: `/articles/POI/settingType`, method: 'GET',
        params, signal
    },
          );
        }, [getArticlesBySettingType])
      }




export const getGetArticlesBySettingTypeQueryKey = (params?: GetArticlesBySettingTypeParams,) => {
    return [
    `/articles/POI/settingType`, ...(params ? [params] : [])
    ] as const;
    }


export const useGetArticlesBySettingTypeQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>, TError = ErrorType<ProblemDetails>>(params: GetArticlesBySettingTypeParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetArticlesBySettingTypeQueryKey(params);

  const getArticlesBySettingType =  useGetArticlesBySettingTypeHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>> = ({ signal }) => getArticlesBySettingType(params, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetArticlesBySettingTypeQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>>
export type GetArticlesBySettingTypeQueryError = ErrorType<ProblemDetails>


export function useGetArticlesBySettingType<TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>, TError = ErrorType<ProblemDetails>>(
 params: GetArticlesBySettingTypeParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetArticlesBySettingType<TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>, TError = ErrorType<ProblemDetails>>(
 params: GetArticlesBySettingTypeParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetArticlesBySettingType<TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>, TError = ErrorType<ProblemDetails>>(
 params: GetArticlesBySettingTypeParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetArticlesBySettingType<TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>, TError = ErrorType<ProblemDetails>>(
 params: GetArticlesBySettingTypeParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesBySettingTypeHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetArticlesBySettingTypeQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Get all existing articles
 */
export const useGetAllArticlesHook = () => {
        const getAllArticles = useCustomAppClient<GetAllArticlesResponse>();

        return useCallback((
    params: GetAllArticlesParams,
 signal?: AbortSignal
) => {


        return getAllArticles(
          {url: `/articles`, method: 'GET',
        params, signal
    },
          );
        }, [getAllArticles])
      }




export const getGetAllArticlesQueryKey = (params?: GetAllArticlesParams,) => {
    return [
    `/articles`, ...(params ? [params] : [])
    ] as const;
    }


export const useGetAllArticlesQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>, TError = ErrorType<ProblemDetails>>(params: GetAllArticlesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetAllArticlesQueryKey(params);

  const getAllArticles =  useGetAllArticlesHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>> = ({ signal }) => getAllArticles(params, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetAllArticlesQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>>
export type GetAllArticlesQueryError = ErrorType<ProblemDetails>


export function useGetAllArticles<TData = Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>, TError = ErrorType<ProblemDetails>>(
 params: GetAllArticlesParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAllArticles<TData = Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>, TError = ErrorType<ProblemDetails>>(
 params: GetAllArticlesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetAllArticles<TData = Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>, TError = ErrorType<ProblemDetails>>(
 params: GetAllArticlesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetAllArticles<TData = Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>, TError = ErrorType<ProblemDetails>>(
 params: GetAllArticlesParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetAllArticlesHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetAllArticlesQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Create or update article
 */
export const useCreateArticleHook = () => {
        const createArticle = useCustomAppClient<GetArticleByIdResponse>();

        return useCallback((
    createArticleModel: CreateArticleModel,
 signal?: AbortSignal
) => {


        return createArticle(
          {url: `/articles`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createArticleModel, signal
    },
          );
        }, [createArticle])
      }




export const getCreateArticleMutationKey = () => ['createArticle'] as const;

export const useCreateArticleMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateArticleHook>>>, TError,CreateArticleMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateArticleHook>>>, TError,CreateArticleMutationVariables, TContext> => {

const mutationKey = getCreateArticleMutationKey();
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const createArticle =  useCreateArticleHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useCreateArticleHook>>>, CreateArticleMutationVariables> = (props) => {
          const {data} = props ?? {};

          return  createArticle(data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateArticleMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useCreateArticleHook>>>>
    export type CreateArticleMutationBody = CreateArticleModel
    export type CreateArticleMutationError = ErrorType<ProblemDetails>
    export type CreateArticleMutationVariables = {data: CreateArticleModel}

    export const useCreateArticle = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useCreateArticleHook>>>, TError,CreateArticleMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useCreateArticleHook>>>,
        TError,
        CreateArticleMutationVariables,
        TContext
      > => {
      return useMutation(useCreateArticleMutationOptions(options), queryClient);
    }
    /**
 * Create or update CAD transformation for a prefab by CAD origin type
 */
export const useSavePrefabTransformationForCadHook = () => {
        const savePrefabTransformationForCad = useCustomAppClient<SavePrefabTransformationForCadResponse>();

        return useCallback((
    prefabId: string,
    savePrefabTransformationForCadModel: SavePrefabTransformationForCadModel,
 signal?: AbortSignal
) => {


        return savePrefabTransformationForCad(
          {url: `/articles/prefabs/${prefabId}/transformations`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: savePrefabTransformationForCadModel, signal
    },
          );
        }, [savePrefabTransformationForCad])
      }




export const getSavePrefabTransformationForCadMutationKey = () => ['savePrefabTransformationForCad'] as const;

export const useSavePrefabTransformationForCadMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSavePrefabTransformationForCadHook>>>, TError,SavePrefabTransformationForCadMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSavePrefabTransformationForCadHook>>>, TError,SavePrefabTransformationForCadMutationVariables, TContext> => {

const mutationKey = getSavePrefabTransformationForCadMutationKey();
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const savePrefabTransformationForCad =  useSavePrefabTransformationForCadHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useSavePrefabTransformationForCadHook>>>, SavePrefabTransformationForCadMutationVariables> = (props) => {
          const {prefabId,data} = props ?? {};

          return  savePrefabTransformationForCad(prefabId,data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SavePrefabTransformationForCadMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useSavePrefabTransformationForCadHook>>>>
    export type SavePrefabTransformationForCadMutationBody = SavePrefabTransformationForCadModel
    export type SavePrefabTransformationForCadMutationError = ErrorType<ProblemDetails>
    export type SavePrefabTransformationForCadMutationVariables = {prefabId: string;data: SavePrefabTransformationForCadModel}

    export const useSavePrefabTransformationForCad = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useSavePrefabTransformationForCadHook>>>, TError,SavePrefabTransformationForCadMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useSavePrefabTransformationForCadHook>>>,
        TError,
        SavePrefabTransformationForCadMutationVariables,
        TContext
      > => {
      return useMutation(useSavePrefabTransformationForCadMutationOptions(options), queryClient);
    }
    /**
 * Get articles by element type
 */
export const useGetArticlesByElementTypeHook = () => {
        const getArticlesByElementType = useCustomAppClient<GetArticlesByElementTypeResponse>();

        return useCallback((
    elementType: number,
 signal?: AbortSignal
) => {


        return getArticlesByElementType(
          {url: `/articles/articleRepresentations/${elementType}`, method: 'GET', signal
    },
          );
        }, [getArticlesByElementType])
      }




export const getGetArticlesByElementTypeQueryKey = (elementType: number,) => {
    return [
    `/articles/articleRepresentations/${elementType}`
    ] as const;
    }


export const useGetArticlesByElementTypeQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>, TError = ErrorType<ProblemDetails>>(elementType: number, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetArticlesByElementTypeQueryKey(elementType);

  const getArticlesByElementType =  useGetArticlesByElementTypeHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>> = ({ signal }) => getArticlesByElementType(elementType, signal);





   return  { queryKey, queryFn, enabled: elementType !== null && elementType !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetArticlesByElementTypeQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>>
export type GetArticlesByElementTypeQueryError = ErrorType<ProblemDetails>


export function useGetArticlesByElementType<TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>, TError = ErrorType<ProblemDetails>>(
 elementType: number, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetArticlesByElementType<TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>, TError = ErrorType<ProblemDetails>>(
 elementType: number, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetArticlesByElementType<TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>, TError = ErrorType<ProblemDetails>>(
 elementType: number, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetArticlesByElementType<TData = Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>, TError = ErrorType<ProblemDetails>>(
 elementType: number, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetArticlesByElementTypeHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetArticlesByElementTypeQueryOptions(elementType,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Get main representation of article by id
 */
export const useGetMainRepresentationByArticleIdHook = () => {
        const getMainRepresentationByArticleId = useCustomAppClient<GetMainRepresentationByArticleIdResponse>();

        return useCallback((
    id: string,
    params: GetMainRepresentationByArticleIdParams,
 signal?: AbortSignal
) => {


        return getMainRepresentationByArticleId(
          {url: `/articles/articleRepresentations/main/${id}`, method: 'GET',
        params, signal
    },
          );
        }, [getMainRepresentationByArticleId])
      }




export const getGetMainRepresentationByArticleIdQueryKey = (id: string,
    params?: GetMainRepresentationByArticleIdParams,) => {
    return [
    `/articles/articleRepresentations/main/${id}`, ...(params ? [params] : [])
    ] as const;
    }


export const useGetMainRepresentationByArticleIdQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>, TError = ErrorType<ProblemDetails>>(id: string,
    params: GetMainRepresentationByArticleIdParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetMainRepresentationByArticleIdQueryKey(id,params);

  const getMainRepresentationByArticleId =  useGetMainRepresentationByArticleIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>> = ({ signal }) => getMainRepresentationByArticleId(id,params, signal);





   return  { queryKey, queryFn, enabled: id !== null && id !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetMainRepresentationByArticleIdQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>>
export type GetMainRepresentationByArticleIdQueryError = ErrorType<ProblemDetails>


export function useGetMainRepresentationByArticleId<TData = Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    params: GetMainRepresentationByArticleIdParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMainRepresentationByArticleId<TData = Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    params: GetMainRepresentationByArticleIdParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetMainRepresentationByArticleId<TData = Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    params: GetMainRepresentationByArticleIdParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetMainRepresentationByArticleId<TData = Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>, TError = ErrorType<ProblemDetails>>(
 id: string,
    params: GetMainRepresentationByArticleIdParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetMainRepresentationByArticleIdHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetMainRepresentationByArticleIdQueryOptions(id,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Delete the main representation of an article by id
 */
export const useDeletePrefabsByArticleIdHook = () => {
        const deletePrefabsByArticleId = useCustomAppClient<void>();

        return useCallback((
    id: string,
 signal?: AbortSignal
) => {


        return deletePrefabsByArticleId(
          {url: `/articles/articleRepresentations/main/${id}`, method: 'DELETE', signal
    },
          );
        }, [deletePrefabsByArticleId])
      }




export const getDeletePrefabsByArticleIdMutationKey = () => ['deletePrefabsByArticleId'] as const;

export const useDeletePrefabsByArticleIdMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeletePrefabsByArticleIdHook>>>, TError,DeletePrefabsByArticleIdMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeletePrefabsByArticleIdHook>>>, TError,DeletePrefabsByArticleIdMutationVariables, TContext> => {

const mutationKey = getDeletePrefabsByArticleIdMutationKey();
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const deletePrefabsByArticleId =  useDeletePrefabsByArticleIdHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useDeletePrefabsByArticleIdHook>>>, DeletePrefabsByArticleIdMutationVariables> = (props) => {
          const {id} = props ?? {};

          return  deletePrefabsByArticleId(id,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeletePrefabsByArticleIdMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useDeletePrefabsByArticleIdHook>>>>

    export type DeletePrefabsByArticleIdMutationError = ErrorType<ProblemDetails>
    export type DeletePrefabsByArticleIdMutationVariables = {id: string}

    export const useDeletePrefabsByArticleId = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useDeletePrefabsByArticleIdHook>>>, TError,DeletePrefabsByArticleIdMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useDeletePrefabsByArticleIdHook>>>,
        TError,
        DeletePrefabsByArticleIdMutationVariables,
        TContext
      > => {
      return useMutation(useDeletePrefabsByArticleIdMutationOptions(options), queryClient);
    }
    /**
 * Get lightweight prefab summaries (id, name, contentHash) for an article � use for client-side change detection without loading the full graph
 */
export const useGetPrefabSummariesByArticleIdHook = () => {
        const getPrefabSummariesByArticleId = useCustomAppClient<GetPrefabSummariesByArticleIdResponse>();

        return useCallback((
    articleId: string,
 signal?: AbortSignal
) => {


        return getPrefabSummariesByArticleId(
          {url: `/articles/articleRepresentations/main/${articleId}/summaries`, method: 'GET', signal
    },
          );
        }, [getPrefabSummariesByArticleId])
      }




export const getGetPrefabSummariesByArticleIdQueryKey = (articleId: string,) => {
    return [
    `/articles/articleRepresentations/main/${articleId}/summaries`
    ] as const;
    }


export const useGetPrefabSummariesByArticleIdQueryOptions = <TData = Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>, TError = ErrorType<ProblemDetails>>(articleId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>, TError, TData>>, }
) => {

const {query: queryOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPrefabSummariesByArticleIdQueryKey(articleId);

  const getPrefabSummariesByArticleId =  useGetPrefabSummariesByArticleIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>> = ({ signal }) => getPrefabSummariesByArticleId(articleId, signal);





   return  { queryKey, queryFn, enabled: articleId !== null && articleId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetPrefabSummariesByArticleIdQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>>
export type GetPrefabSummariesByArticleIdQueryError = ErrorType<ProblemDetails>


export function useGetPrefabSummariesByArticleId<TData = Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>, TError = ErrorType<ProblemDetails>>(
 articleId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetPrefabSummariesByArticleId<TData = Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>, TError = ErrorType<ProblemDetails>>(
 articleId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>,
          TError,
          Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>
        > , 'initialData'
      >, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetPrefabSummariesByArticleId<TData = Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>, TError = ErrorType<ProblemDetails>>(
 articleId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }

export function useGetPrefabSummariesByArticleId<TData = Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>, TError = ErrorType<ProblemDetails>>(
 articleId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetPrefabSummariesByArticleIdHook>>>, TError, TData>>, }
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = useGetPrefabSummariesByArticleIdQueryOptions(articleId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return withQueryKey(query, queryOptions.queryKey);
}






/**
 * Import a prefab with full data (layers, children, transform, asset reference) as the main representation of an article
 */
export const useImportPrefabByArticleIdHook = () => {
        const importPrefabByArticleId = useCustomAppClient<GetMainRepresentationByArticleIdResponse>();

        return useCallback((
    createPrefabModel: CreatePrefabModel,
    params: ImportPrefabByArticleIdParams,
 signal?: AbortSignal
) => {


        return importPrefabByArticleId(
          {url: `/articles/articleRepresentations/main`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createPrefabModel,
        params, signal
    },
          );
        }, [importPrefabByArticleId])
      }




export const getImportPrefabByArticleIdMutationKey = () => ['importPrefabByArticleId'] as const;

export const useImportPrefabByArticleIdMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useImportPrefabByArticleIdHook>>>, TError,ImportPrefabByArticleIdMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useImportPrefabByArticleIdHook>>>, TError,ImportPrefabByArticleIdMutationVariables, TContext> => {

const mutationKey = getImportPrefabByArticleIdMutationKey();
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const importPrefabByArticleId =  useImportPrefabByArticleIdHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useImportPrefabByArticleIdHook>>>, ImportPrefabByArticleIdMutationVariables> = (props) => {
          const {data,params} = props ?? {};

          return  importPrefabByArticleId(data,params,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ImportPrefabByArticleIdMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useImportPrefabByArticleIdHook>>>>
    export type ImportPrefabByArticleIdMutationBody = CreatePrefabModel
    export type ImportPrefabByArticleIdMutationError = ErrorType<ProblemDetails>
    export type ImportPrefabByArticleIdMutationVariables = {data: CreatePrefabModel;params: ImportPrefabByArticleIdParams}

    export const useImportPrefabByArticleId = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useImportPrefabByArticleIdHook>>>, TError,ImportPrefabByArticleIdMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useImportPrefabByArticleIdHook>>>,
        TError,
        ImportPrefabByArticleIdMutationVariables,
        TContext
      > => {
      return useMutation(useImportPrefabByArticleIdMutationOptions(options), queryClient);
    }
    /**
 * Insert or update a single prefab by name within an article � use for targeted updates instead of replacing the full set
 */
export const useUpdatePrefabByArticleIdHook = () => {
        const updatePrefabByArticleId = useCustomAppClient<void>();

        return useCallback((
    articleId: string,
    updatePrefabModel: UpdatePrefabModel,
 signal?: AbortSignal
) => {


        return updatePrefabByArticleId(
          {url: `/articles/articleRepresentations/main/${articleId}/prefab`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: updatePrefabModel, signal
    },
          );
        }, [updatePrefabByArticleId])
      }




export const getUpdatePrefabByArticleIdMutationKey = () => ['updatePrefabByArticleId'] as const;

export const useUpdatePrefabByArticleIdMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useUpdatePrefabByArticleIdHook>>>, TError,UpdatePrefabByArticleIdMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useUpdatePrefabByArticleIdHook>>>, TError,UpdatePrefabByArticleIdMutationVariables, TContext> => {

const mutationKey = getUpdatePrefabByArticleIdMutationKey();
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const updatePrefabByArticleId =  useUpdatePrefabByArticleIdHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useUpdatePrefabByArticleIdHook>>>, UpdatePrefabByArticleIdMutationVariables> = (props) => {
          const {articleId,data} = props ?? {};

          return  updatePrefabByArticleId(articleId,data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePrefabByArticleIdMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useUpdatePrefabByArticleIdHook>>>>
    export type UpdatePrefabByArticleIdMutationBody = UpdatePrefabModel
    export type UpdatePrefabByArticleIdMutationError = ErrorType<ProblemDetails>
    export type UpdatePrefabByArticleIdMutationVariables = {articleId: string;data: UpdatePrefabModel}

    export const useUpdatePrefabByArticleId = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useUpdatePrefabByArticleIdHook>>>, TError,UpdatePrefabByArticleIdMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useUpdatePrefabByArticleIdHook>>>,
        TError,
        UpdatePrefabByArticleIdMutationVariables,
        TContext
      > => {
      return useMutation(useUpdatePrefabByArticleIdMutationOptions(options), queryClient);
    }
    /**
 * Replace all prefabs of an article by id
 */
export const useReplacePrefabsByArticleIdHook = () => {
        const replacePrefabsByArticleId = useCustomAppClient<void>();

        return useCallback((
    id: string,
    createPrefabModel: CreatePrefabModel[],
 signal?: AbortSignal
) => {


        return replacePrefabsByArticleId(
          {url: `/articles/articleRepresentations/main/${id}/replace`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createPrefabModel, signal
    },
          );
        }, [replacePrefabsByArticleId])
      }




export const getReplacePrefabsByArticleIdMutationKey = () => ['replacePrefabsByArticleId'] as const;

export const useReplacePrefabsByArticleIdMutationOptions = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useReplacePrefabsByArticleIdHook>>>, TError,ReplacePrefabsByArticleIdMutationVariables, TContext>, }
): UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useReplacePrefabsByArticleIdHook>>>, TError,ReplacePrefabsByArticleIdMutationVariables, TContext> => {

const mutationKey = getReplacePrefabsByArticleIdMutationKey();
const {mutation: mutationOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }};

      const replacePrefabsByArticleId =  useReplacePrefabsByArticleIdHook()


      const mutationFn: MutationFunction<Awaited<ReturnType<ReturnType<typeof useReplacePrefabsByArticleIdHook>>>, ReplacePrefabsByArticleIdMutationVariables> = (props) => {
          const {id,data} = props ?? {};

          return  replacePrefabsByArticleId(id,data,)
        }






  return  { mutationFn, ...mutationOptions }}

    export type ReplacePrefabsByArticleIdMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useReplacePrefabsByArticleIdHook>>>>
    export type ReplacePrefabsByArticleIdMutationBody = CreatePrefabModel[]
    export type ReplacePrefabsByArticleIdMutationError = ErrorType<ProblemDetails>
    export type ReplacePrefabsByArticleIdMutationVariables = {id: string;data: CreatePrefabModel[]}

    export const useReplacePrefabsByArticleId = <TError = ErrorType<ProblemDetails>,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<ReturnType<typeof useReplacePrefabsByArticleIdHook>>>, TError,ReplacePrefabsByArticleIdMutationVariables, TContext>, }
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<ReturnType<typeof useReplacePrefabsByArticleIdHook>>>,
        TError,
        ReplacePrefabsByArticleIdMutationVariables,
        TContext
      > => {
      return useMutation(useReplacePrefabsByArticleIdMutationOptions(options), queryClient);
    }
